/**
 * Injection Pack 查看器/编辑器
 * 展示和编辑 Facet 的结构化上下文包
 * 
 * 数据结构 (v9.2 Learning Pipeline 生成):
 * - persona_core: 身份声明、核心特质、价值观、默认姿态、禁用表述
 * - style_profile: 签名短语、句子形态、语调规则、语言标识、标点习惯
 * - emotion_patterns: 情绪触发模式列表（含强度）
 * - boundary_guard: 硬边界、软边界
 * - output_format_hint: 输出格式提示（v10.3 新增）
 * 
 * v9.2.2: 支持人工编辑，参考设计文档 3.3.3 节
 * v10.3: 新增 language, punctuation_habits, intensity, output_format_hint 字段
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import { Slider } from '@/shared/components/ui/slider'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/components/ui/collapsible'
import { cn } from '@/shared/lib/utils'
import {
  User,
  Palette,
  Heart,
  Shield,
  ChevronDown,
  ChevronRight,
  BarChart3,
  MessageSquare,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Pencil,
  Save,
  RotateCcw,
  Plus,
  X,
  Loader2,
} from 'lucide-react'
import { useState, useCallback, useEffect } from 'react'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface InjectionPackViewerProps {
  injectionPack: any
  featureStore?: any
  editable?: boolean
  onSave?: (data: any) => Promise<void>
}

/**
 * 可折叠的内容块
 */
function CollapsibleSection({
  icon: Icon,
  title,
  description,
  children,
  defaultOpen = true,
  className,
  actions,
}: {
  icon: React.ElementType
  title: string
  description?: string
  children: React.ReactNode
  defaultOpen?: boolean
  className?: string
  actions?: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className={cn(
        'flex items-center justify-between p-4 bg-slate-50 rounded-lg',
        className
      )}>
        <CollapsibleTrigger className="flex items-center gap-3 flex-1 hover:bg-slate-100 -m-2 p-2 rounded transition-colors">
          <Icon className="h-5 w-5 text-violet-500" />
          <div className="text-left">
            <span className="font-medium text-slate-800">{title}</span>
            {description && (
              <span className="text-sm text-slate-500 ml-2">{description}</span>
            )}
          </div>
          {open ? (
            <ChevronDown className="h-4 w-4 text-slate-400 ml-auto" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-400 ml-auto" />
          )}
        </CollapsibleTrigger>
        {actions && <div className="ml-2">{actions}</div>}
      </div>
      <CollapsibleContent className="pt-4">
        {children}
      </CollapsibleContent>
    </Collapsible>
  )
}

/**
 * 标签列表（可编辑）
 */
function TagList({ 
  tags, 
  color = 'violet',
  editable = false,
  onChange,
}: { 
  tags: string[]
  color?: string
  editable?: boolean
  onChange?: (tags: string[]) => void
}) {
  const [newTag, setNewTag] = useState('')

  if (!tags || tags.length === 0) {
    if (!editable) return <span className="text-slate-400">无</span>
  }

  const handleAddTag = () => {
    if (newTag.trim() && onChange) {
      onChange([...tags, newTag.trim()])
      setNewTag('')
    }
  }

  const handleRemoveTag = (index: number) => {
    if (onChange) {
      onChange(tags.filter((_, i) => i !== index))
    }
  }
  
  return (
    <div className="flex flex-wrap gap-2 items-center">
      {(tags || []).map((tag, i) => (
        <Badge
          key={i}
          variant="secondary"
          className={cn(
            'flex items-center gap-1',
            color === 'violet' && 'bg-violet-100 text-violet-700',
            color === 'red' && 'bg-red-100 text-red-700',
            color === 'amber' && 'bg-amber-100 text-amber-700',
            color === 'emerald' && 'bg-emerald-100 text-emerald-700',
            color === 'blue' && 'bg-blue-100 text-blue-700',
          )}
        >
          {tag}
          {editable && (
            <button
              type="button"
              onClick={() => handleRemoveTag(i)}
              className="ml-1 hover:bg-black/10 rounded-full p-0.5"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </Badge>
      ))}
      {editable && (
        <div className="flex items-center gap-1">
          <Input
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
            placeholder="添加..."
            className="h-7 w-24 text-xs"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleAddTag}
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * 键值对展示
 */
function KeyValueItem({ label, value, className }: { label: string; value: React.ReactNode; className?: string }) {
  return (
    <div className={cn('space-y-1', className)}>
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="text-slate-800">{value}</dd>
    </div>
  )
}

/**
 * 姿态滑块（可编辑）
 */
function StanceSlider({ 
  label, 
  value, 
  editable = false,
  onChange,
}: { 
  label: string
  value: number
  editable?: boolean
  onChange?: (value: number) => void
}) {
  const percentage = Math.round(value * 100)
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-slate-500">{label}</span>
        <span className="text-slate-700 font-medium">{percentage}%</span>
      </div>
      {editable ? (
        <Slider
          value={value * 100}
          onValueChange={(v) => onChange?.(v / 100)}
          max={100}
          step={5}
          className="w-full"
        />
      ) : (
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-violet-500 rounded-full transition-all"
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}
    </div>
  )
}

/**
 * Injection Pack 查看器/编辑器
 */
export function InjectionPackViewer({ 
  injectionPack, 
  featureStore,
  editable = false,
  onSave,
}: InjectionPackViewerProps) {
  // 编辑状态
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState<any>(null)
  const [saving, setSaving] = useState(false)

  // 初始化编辑数据
  useEffect(() => {
    if (injectionPack) {
      setEditData(JSON.parse(JSON.stringify(injectionPack)))
    }
  }, [injectionPack])

  // 如果 injectionPack 完全为空，显示提示
  if (!injectionPack) {
    return (
      <div className="p-8 text-center text-slate-400">
        <p>暂无人格配置数据</p>
        <p className="text-sm mt-2">请先完成学习任务</p>
      </div>
    )
  }

  // 使用编辑数据或原始数据
  const data = isEditing && editData ? editData : injectionPack

  // 防御性检查：提取嵌套属性
  const personaCore = data?.persona_core || {}
  const styleProfile = data?.style_profile || {}
  const emotionPatterns = data?.emotion_patterns || []
  const boundaryGuard = data?.boundary_guard || {}
  const outputFormatHint = data?.output_format_hint || {}  // v10.3 新增

  // style_profile 子字段
  const signaturePhrases = styleProfile?.signature_phrases || {}
  const sentenceShape = styleProfile?.sentence_shape || {}
  const toneRules = styleProfile?.tone_rules || []
  const language = styleProfile?.language || ''  // v10.3 新增
  const punctuationHabits = styleProfile?.punctuation_habits || {}  // v10.3 新增

  // persona_core 子字段
  const defaultStance = personaCore?.default_stance || {}

  // 更新嵌套字段
  const updateField = useCallback((path: string[], value: any) => {
    setEditData((prev: any) => {
      const newData = JSON.parse(JSON.stringify(prev))
      let current = newData
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) {
          current[path[i]] = {}
        }
        current = current[path[i]]
      }
      current[path[path.length - 1]] = value
      return newData
    })
  }, [])

  // 保存
  const handleSave = useCallback(async () => {
    if (!onSave || !editData) return
    setSaving(true)
    try {
      await onSave(editData)
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to save:', error)
    } finally {
      setSaving(false)
    }
  }, [onSave, editData])

  // 重置
  const handleReset = useCallback(() => {
    setEditData(JSON.parse(JSON.stringify(injectionPack)))
    setIsEditing(false)
  }, [injectionPack])

  // 编辑/保存按钮
  const editActions = editable && (
    <div className="flex items-center gap-2 mb-4">
      {isEditing ? (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            disabled={saving}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            重置
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            保存
          </Button>
        </>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsEditing(true)}
        >
          <Pencil className="h-4 w-4 mr-2" />
          编辑
        </Button>
      )}
    </div>
  )

  return (
    <div className="space-y-4">
      {/* 编辑操作按钮 */}
      {editActions}

      {/* Persona Core - 核心人格 */}
      <Card className={isEditing ? 'ring-2 ring-violet-200' : ''}>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="h-5 w-5 text-violet-500" />
            核心人格
          </CardTitle>
          <CardDescription>persona_core - 身份声明、核心特质和价值观</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 身份声明 */}
          <KeyValueItem
            label="身份声明"
            value={
              isEditing ? (
                <Textarea
                  value={personaCore.identity_statement || ''}
                  onChange={(e) => updateField(['persona_core', 'identity_statement'], e.target.value)}
                  className="text-sm"
                  rows={2}
                />
              ) : (
                <p className="text-sm bg-slate-50 p-3 rounded-lg border-l-4 border-violet-500">
                  {personaCore.identity_statement || '无'}
                </p>
              )
            }
          />
          
          {/* 核心特质 */}
          <KeyValueItem
            label="核心特质"
            value={
              <TagList 
                tags={personaCore.core_traits || []} 
                editable={isEditing}
                onChange={(tags) => updateField(['persona_core', 'core_traits'], tags)}
              />
            }
          />
          
          {/* 价值观 */}
          <KeyValueItem
            label="价值观"
            value={
              <TagList 
                tags={personaCore.values || []} 
                color="blue"
                editable={isEditing}
                onChange={(tags) => updateField(['persona_core', 'values'], tags)}
              />
            }
          />
          
          {/* 禁用表述 */}
          <KeyValueItem
            label="禁用表述"
            value={
              <TagList 
                tags={personaCore.taboo_statements || []} 
                color="red"
                editable={isEditing}
                onChange={(tags) => updateField(['persona_core', 'taboo_statements'], tags)}
              />
            }
          />
          
          {/* 默认姿态 */}
          {(Object.keys(defaultStance).length > 0 || isEditing) && (
            <div className="space-y-2 pt-2">
              <span className="text-sm text-slate-500">默认姿态</span>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StanceSlider 
                  label="正式度" 
                  value={defaultStance.formality ?? 0.5}
                  editable={isEditing}
                  onChange={(v) => updateField(['persona_core', 'default_stance', 'formality'], v)}
                />
                <StanceSlider 
                  label="直接度" 
                  value={defaultStance.directness ?? 0.5}
                  editable={isEditing}
                  onChange={(v) => updateField(['persona_core', 'default_stance', 'directness'], v)}
                />
                <StanceSlider 
                  label="幽默度" 
                  value={defaultStance.humor ?? 0.5}
                  editable={isEditing}
                  onChange={(v) => updateField(['persona_core', 'default_stance', 'humor'], v)}
                />
                <StanceSlider 
                  label="温暖度" 
                  value={defaultStance.warmth ?? 0.5}
                  editable={isEditing}
                  onChange={(v) => updateField(['persona_core', 'default_stance', 'warmth'], v)}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Style Profile - 风格配置 */}
      <Card className={isEditing ? 'ring-2 ring-violet-200' : ''}>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Palette className="h-5 w-5 text-violet-500" />
            风格配置
          </CardTitle>
          <CardDescription>style_profile - 说话风格和语言模式</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* v10.3: 语言标识 */}
          {(language || isEditing) && (
            <KeyValueItem
              label="语言标识"
              value={
                isEditing ? (
                  <Input
                    value={language}
                    onChange={(e) => updateField(['style_profile', 'language'], e.target.value)}
                    placeholder="如 zh-CN"
                    className="w-32"
                  />
                ) : (
                  <Badge variant="outline">{language || '未设置'}</Badge>
                )
              }
            />
          )}

          {/* v10.3: 标点使用习惯 */}
          {(Object.keys(punctuationHabits).length > 0 || isEditing) && (
            <CollapsibleSection
              icon={MessageSquare}
              title="标点使用习惯"
              description="punctuation_habits"
              defaultOpen={true}
            >
              <div className="pl-4 space-y-2">
                {Object.entries(punctuationHabits).map(([punct, ratio]) => (
                  <div key={punct} className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{punct}</span>
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <Slider
                          value={(ratio as number) * 100}
                          onValueChange={(v) => {
                            const newHabits = { ...punctuationHabits, [punct]: v / 100 }
                            updateField(['style_profile', 'punctuation_habits'], newHabits)
                          }}
                          max={100}
                          step={5}
                          className="w-24"
                        />
                        <span className="text-xs w-10 text-right">{Math.round((ratio as number) * 100)}%</span>
                      </div>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        {Math.round((ratio as number) * 100)}%
                      </Badge>
                    )}
                  </div>
                ))}
                {Object.keys(punctuationHabits).length === 0 && !isEditing && (
                  <span className="text-slate-400">无</span>
                )}
              </div>
            </CollapsibleSection>
          )}

          {/* 签名短语 */}
          <CollapsibleSection
            icon={MessageSquare}
            title="签名短语"
            description="signature_phrases"
            defaultOpen={true}
          >
            <div className="space-y-4 pl-4">
              <KeyValueItem
                label="推荐使用"
                value={
                  isEditing ? (
                    <TagList
                      tags={signaturePhrases.allowed || []}
                      color="emerald"
                      editable={true}
                      onChange={(tags) => updateField(['style_profile', 'signature_phrases', 'allowed'], tags)}
                    />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {(signaturePhrases.allowed || []).map((phrase: string, i: number) => (
                        <Badge key={i} className="bg-emerald-100 text-emerald-700 flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          {phrase}
                        </Badge>
                      ))}
                      {(!signaturePhrases.allowed || signaturePhrases.allowed.length === 0) && (
                        <span className="text-slate-400">无</span>
                      )}
                    </div>
                  )
                }
              />
              <KeyValueItem
                label="避免使用"
                value={
                  isEditing ? (
                    <TagList
                      tags={signaturePhrases.disallowed || []}
                      color="red"
                      editable={true}
                      onChange={(tags) => updateField(['style_profile', 'signature_phrases', 'disallowed'], tags)}
                    />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {(signaturePhrases.disallowed || []).map((phrase: string, i: number) => (
                        <Badge key={i} className="bg-red-100 text-red-700 flex items-center gap-1">
                          <XCircle className="h-3 w-3" />
                          {phrase}
                        </Badge>
                      ))}
                      {(!signaturePhrases.disallowed || signaturePhrases.disallowed.length === 0) && (
                        <span className="text-slate-400">无</span>
                      )}
                    </div>
                  )
                }
              />
            </div>
          </CollapsibleSection>

          {/* 句子形态 */}
          <CollapsibleSection
            icon={BarChart3}
            title="句子形态"
            description="sentence_shape"
            defaultOpen={true}
          >
            <div className="grid grid-cols-2 gap-4 pl-4">
              <KeyValueItem
                label="短句比例"
                value={
                  isEditing ? (
                    <div className="flex items-center gap-2">
                      <Slider
                        value={(sentenceShape.short_sentence_ratio ?? 0.5) * 100}
                        onValueChange={(v) => updateField(['style_profile', 'sentence_shape', 'short_sentence_ratio'], v / 100)}
                        max={100}
                        step={5}
                        className="flex-1"
                      />
                      <span className="text-sm w-12 text-right">
                        {Math.round((sentenceShape.short_sentence_ratio ?? 0.5) * 100)}%
                      </span>
                    </div>
                  ) : (
                    sentenceShape.short_sentence_ratio !== undefined 
                      ? `${Math.round(sentenceShape.short_sentence_ratio * 100)}%`
                      : '无'
                  )
                }
              />
              <KeyValueItem
                label="平均句长"
                value={
                  isEditing ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={sentenceShape.avg_length_chars ?? 20}
                        onChange={(e) => updateField(['style_profile', 'sentence_shape', 'avg_length_chars'], Number(e.target.value))}
                        className="w-20"
                      />
                      <span className="text-sm">字</span>
                    </div>
                  ) : (
                    sentenceShape.avg_length_chars !== undefined 
                      ? `${sentenceShape.avg_length_chars} 字`
                      : '无'
                  )
                }
              />
            </div>
          </CollapsibleSection>

          {/* 语调规则 */}
          <CollapsibleSection
            icon={Palette}
            title="语调规则"
            description="tone_rules"
            defaultOpen={true}
          >
            {isEditing ? (
              <div className="space-y-2 pl-4">
                {(toneRules || []).map((rule: string, i: number) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={rule}
                      onChange={(e) => {
                        const newRules = [...(toneRules || [])]
                        newRules[i] = e.target.value
                        updateField(['style_profile', 'tone_rules'], newRules)
                      }}
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-500"
                      onClick={() => {
                        const newRules = toneRules.filter((_: string, j: number) => j !== i)
                        updateField(['style_profile', 'tone_rules'], newRules)
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateField(['style_profile', 'tone_rules'], [...(toneRules || []), ''])}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  添加规则
                </Button>
              </div>
            ) : toneRules.length > 0 ? (
              <ul className="list-disc list-inside pl-4 space-y-2">
                {toneRules.map((rule: string, i: number) => (
                  <li key={i} className="text-sm text-slate-700">{rule}</li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-400 pl-4">无</p>
            )}
          </CollapsibleSection>
        </CardContent>
      </Card>

      {/* Emotion Patterns - 情绪反应 */}
      <Card className={isEditing ? 'ring-2 ring-violet-200' : ''}>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Heart className="h-5 w-5 text-violet-500" />
            情绪反应
          </CardTitle>
          <CardDescription>emotion_patterns - 情绪触发和应对策略</CardDescription>
        </CardHeader>
        <CardContent>
          {Array.isArray(emotionPatterns) && emotionPatterns.length > 0 ? (
            <div className="space-y-4">
              {emotionPatterns.map((pattern: any, i: number) => (
                <div key={i} className="p-4 bg-slate-50 rounded-lg space-y-3">
                  {/* 触发器名称和关键词 */}
                  <div className="flex items-center justify-between">
                    {isEditing ? (
                      <Input
                        value={pattern.trigger_name || ''}
                        onChange={(e) => {
                          const newPatterns = [...emotionPatterns]
                          newPatterns[i] = { ...pattern, trigger_name: e.target.value }
                          updateField(['emotion_patterns'], newPatterns)
                        }}
                        placeholder="触发器名称"
                        className="w-48"
                      />
                    ) : (
                      <span className="font-medium text-slate-800 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                        {pattern.trigger_name || `触发器 ${i + 1}`}
                      </span>
                    )}
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <Input
                          value={pattern.approach || ''}
                          onChange={(e) => {
                            const newPatterns = [...emotionPatterns]
                            newPatterns[i] = { ...pattern, approach: e.target.value }
                            updateField(['emotion_patterns'], newPatterns)
                          }}
                          placeholder="应对策略"
                          className="w-32"
                        />
                      ) : (
                        <Badge className="bg-violet-100 text-violet-700">
                          {pattern.approach || '默认应对'}
                        </Badge>
                      )}
                      {isEditing && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500"
                          onClick={() => {
                            const newPatterns = emotionPatterns.filter((_: any, j: number) => j !== i)
                            updateField(['emotion_patterns'], newPatterns)
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* 触发关键词 */}
                  <div>
                    <span className="text-sm text-slate-500">触发关键词：</span>
                    <div className="mt-1">
                      <TagList
                        tags={pattern.trigger_keywords || []}
                        editable={isEditing}
                        onChange={(tags) => {
                          const newPatterns = [...emotionPatterns]
                          newPatterns[i] = { ...pattern, trigger_keywords: tags }
                          updateField(['emotion_patterns'], newPatterns)
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* 应做和避免 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-emerald-600 font-medium">✓ 应该做</span>
                      <div className="mt-1">
                        <TagList
                          tags={pattern.do_list || []}
                          color="emerald"
                          editable={isEditing}
                          onChange={(tags) => {
                            const newPatterns = [...emotionPatterns]
                            newPatterns[i] = { ...pattern, do_list: tags }
                            updateField(['emotion_patterns'], newPatterns)
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-red-600 font-medium">✗ 避免做</span>
                      <div className="mt-1">
                        <TagList
                          tags={pattern.dont_list || []}
                          color="red"
                          editable={isEditing}
                          onChange={(tags) => {
                            const newPatterns = [...emotionPatterns]
                            newPatterns[i] = { ...pattern, dont_list: tags }
                            updateField(['emotion_patterns'], newPatterns)
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* v10.3: 情绪强度 */}
                  {(pattern.intensity || isEditing) && (
                    <div className="pt-2 border-t">
                      <span className="text-sm text-slate-500 font-medium">情绪强度</span>
                      <div className="mt-2 grid grid-cols-2 gap-4">
                        <StanceSlider
                          label="坚定程度"
                          value={pattern.intensity?.firmness ?? 0.5}
                          editable={isEditing}
                          onChange={(v) => {
                            const newPatterns = [...emotionPatterns]
                            newPatterns[i] = {
                              ...pattern,
                              intensity: { ...(pattern.intensity || {}), firmness: v }
                            }
                            updateField(['emotion_patterns'], newPatterns)
                          }}
                        />
                        <StanceSlider
                          label="温暖程度"
                          value={pattern.intensity?.warmth ?? 0.5}
                          editable={isEditing}
                          onChange={(v) => {
                            const newPatterns = [...emotionPatterns]
                            newPatterns[i] = {
                              ...pattern,
                              intensity: { ...(pattern.intensity || {}), warmth: v }
                            }
                            updateField(['emotion_patterns'], newPatterns)
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {isEditing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateField(['emotion_patterns'], [
                    ...emotionPatterns,
                    { trigger_name: '', approach: '', trigger_keywords: [], do_list: [], dont_list: [] }
                  ])}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  添加情绪触发
                </Button>
              )}
            </div>
          ) : isEditing ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => updateField(['emotion_patterns'], [
                { trigger_name: '', approach: '', trigger_keywords: [], do_list: [], dont_list: [] }
              ])}
            >
              <Plus className="h-4 w-4 mr-2" />
              添加情绪触发
            </Button>
          ) : (
            <p className="text-slate-400">暂无情绪反应数据</p>
          )}
        </CardContent>
      </Card>

      {/* Boundary Guard - 边界规则 */}
      <Card className={isEditing ? 'ring-2 ring-violet-200' : ''}>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="h-5 w-5 text-violet-500" />
            边界规则
          </CardTitle>
          <CardDescription>boundary_guard - 硬边界和软边界</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <KeyValueItem
            label="硬边界（绝对禁止）"
            value={
              <TagList 
                tags={boundaryGuard.hard_boundaries || []} 
                color="red"
                editable={isEditing}
                onChange={(tags) => updateField(['boundary_guard', 'hard_boundaries'], tags)}
              />
            }
          />
          <KeyValueItem
            label="软边界（尽量避免）"
            value={
              <TagList 
                tags={boundaryGuard.soft_boundaries || []} 
                color="amber"
                editable={isEditing}
                onChange={(tags) => updateField(['boundary_guard', 'soft_boundaries'], tags)}
              />
            }
          />
        </CardContent>
      </Card>

      {/* v10.3: Output Format Hint - 输出格式提示 */}
      {(Object.keys(outputFormatHint).length > 0 || isEditing) && (
        <Card className={isEditing ? 'ring-2 ring-violet-200' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-violet-500" />
              输出格式提示
            </CardTitle>
            <CardDescription>output_format_hint - 偏好结构和避免模式</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <KeyValueItem
              label="偏好结构"
              value={
                isEditing ? (
                  <Textarea
                    value={outputFormatHint.preferred || ''}
                    onChange={(e) => updateField(['output_format_hint', 'preferred'], e.target.value)}
                    placeholder="如：简短回复，一次说一件事"
                    rows={2}
                    className="text-sm"
                  />
                ) : outputFormatHint.preferred ? (
                  <p className="text-sm bg-emerald-50 p-3 rounded-lg border-l-4 border-emerald-500">
                    {outputFormatHint.preferred}
                  </p>
                ) : (
                  <span className="text-slate-400">无</span>
                )
              }
            />
            <KeyValueItem
              label="避免模式"
              value={
                Array.isArray(outputFormatHint.avoid) ? (
                  <TagList
                    tags={outputFormatHint.avoid}
                    color="red"
                    editable={isEditing}
                    onChange={(tags) => updateField(['output_format_hint', 'avoid'], tags)}
                  />
                ) : typeof outputFormatHint.avoid === 'string' ? (
                  isEditing ? (
                    <Textarea
                      value={outputFormatHint.avoid}
                      onChange={(e) => updateField(['output_format_hint', 'avoid'], e.target.value)}
                      placeholder="如：长篇大论, 过于正式的措辞"
                      rows={2}
                      className="text-sm"
                    />
                  ) : (
                    <p className="text-sm bg-red-50 p-3 rounded-lg border-l-4 border-red-500">
                      {outputFormatHint.avoid}
                    </p>
                  )
                ) : (
                  <span className="text-slate-400">无</span>
                )
              }
            />
          </CardContent>
        </Card>
      )}

      {/* Feature Store - 统计特征（只读） */}
      {featureStore && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-violet-500" />
              统计特征
            </CardTitle>
            <CardDescription>feature_store - 观测数据和推断结果（只读）</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* 观测数据 */}
            {featureStore.observed && (
              <CollapsibleSection
                icon={BarChart3}
                title="观测数据"
                description="observed"
                defaultOpen={false}
              >
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pl-4">
                  <KeyValueItem
                    label="平均句长"
                    value={`${featureStore.observed.avg_sentence_length?.toFixed(1) ?? '-'} 字`}
                  />
                  <KeyValueItem
                    label="提问比例"
                    value={featureStore.observed.question_ratio != null 
                      ? `${(featureStore.observed.question_ratio * 100).toFixed(1)}%` 
                      : '-'}
                  />
                  <KeyValueItem
                    label="Emoji 频率"
                    value={featureStore.observed.emoji_frequency != null
                      ? `${(featureStore.observed.emoji_frequency * 100).toFixed(1)}%`
                      : '-'}
                  />
                  <KeyValueItem
                    label="高频词"
                    value={
                      featureStore.observed.word_frequency && Object.keys(featureStore.observed.word_frequency).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(featureStore.observed.word_frequency)
                            .sort((a, b) => (b[1] as number) - (a[1] as number))
                            .slice(0, 5)
                            .map(([word, count]) => (
                              <Badge key={word} variant="outline" className="text-xs">
                                {word}({count as number})
                              </Badge>
                            ))}
                        </div>
                      ) : '无'
                    }
                  />
                </div>
              </CollapsibleSection>
            )}

            {/* 推断结果 */}
            {featureStore.inferred && (
              <CollapsibleSection
                icon={BarChart3}
                title="推断结果"
                description="inferred"
                defaultOpen={false}
              >
                <div className="space-y-4 pl-4">
                  <KeyValueItem
                    label="人格特质"
                    value={<TagList tags={featureStore.inferred.personality_traits || []} />}
                  />
                  <KeyValueItem
                    label="沟通风格"
                    value={featureStore.inferred.communication_style || '无'}
                  />
                </div>
              </CollapsibleSection>
            )}
          </CardContent>
        </Card>
      )}

      {/* 底部保存按钮（编辑模式） */}
      {isEditing && (
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={saving}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            重置
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            保存更改
          </Button>
        </div>
      )}
    </div>
  )
}
